import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { footerInfo, navLinks } from '../data/landing';
import { BrandLogo } from './BrandLogo';
import { NavMenu } from './NavMenu';
import { SectionLink } from './SectionLink';
const ease = [0.23, 1, 0.32, 1] as const;
export function Nav() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Close on Escape or click outside
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);
  return <header className="pointer-events-none fixed inset-x-0 top-4 z-50 flex justify-center px-5 md:top-5 md:px-3" data-id="element-243" data-mp="1u43kgdc3czfs">
      <div ref={rootRef} className="flex w-full max-w-[320px] flex-col items-center md:w-auto md:max-w-none" data-id="element-244" data-mp="1p61s11srznrn">
        {/* Floating segmented dock — frosted, always visible; spans edge to edge on phones */}
        <nav aria-label="Main" className="pointer-events-auto flex h-[68px] w-full items-center justify-between gap-[5px] rounded-card md:w-auto md:justify-start border border-white/10 bg-[#1c1c1c]/60 p-[5px] shadow-[0_8px_30px_rgba(0,0,0,0.18)] backdrop-blur-xl backdrop-saturate-150" data-id="element-245" data-mp="2htefde6rfzm0">
          {/* Logo tile */}
          <SectionLink hash="#top" aria-label="LAEBEL home" className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-control bg-black/40 text-white transition-colors duration-150 hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-246" data-mp="30kfcrxs8fn3g">
            <BrandLogo className="h-6 w-6" data-id="element-247" data-mp="xwp4dgvpxkxf" />
          </SectionLink>

          {/* Phones: just the email between the logo and menu tiles */}
          <a href={`mailto:${footerInfo.email}`} className="min-w-0 flex-1 truncate px-2 text-center text-[15px] text-white/85 transition-colors duration-150 hover:text-white focus:outline-none focus-visible:underline md:hidden" data-id="element-248" data-mp="k498r155cp6j">
            {footerInfo.email}
          </a>

          {/* Link pills */}
          {/* Desktop only — phones and tablets get just the logo and menu tiles */}
          <ul className="hidden h-[56px] items-stretch gap-[5px] rounded-control bg-black/25 p-[5px] lg:flex" data-id="element-249" data-mp="66axi4m4h8v9">
            {navLinks.map(link => <li key={link.label} className="flex" data-id="element-250" data-mp="1vm1xa3stub4g">
                <SectionLink hash={link.href} className="flex items-center whitespace-nowrap rounded-chip border border-white/10 px-3 text-[14px] text-white/85 transition-colors duration-150 hover:border-white/25 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-251" data-mp="20kbyav18g5xz">
                  {link.label}
                </SectionLink>
              </li>)}
          </ul>

          {/* Menu toggle — two lines that morph into a close mark */}
          <button ref={toggleRef} type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls="site-menu" aria-label={open ? 'Close menu' : 'Open menu'} className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-control bg-[#f2f2f2] text-[#141414] transition-colors duration-150 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-252" data-mp="2tdgr9o6yeo30">
            {/* Two identical 2px lines on whole-pixel positions, 4px apart */}
            <span className="relative block h-[8px] w-[20px]" aria-hidden="true" data-id="element-253" data-mp="15g7nezidfry3">
              <motion.span className="absolute left-0 top-0 block h-[2px] w-[20px] bg-current" animate={open ? {
              y: 3,
              rotate: 45
            } : {
              y: 0,
              rotate: 0
            }} transition={{
              duration: 0.2,
              ease
            }} data-id="element-254" data-mp="1afkj89r809ct" />
              <motion.span className="absolute left-0 top-[6px] block h-[2px] w-[20px] bg-current" animate={open ? {
              y: -3,
              rotate: -45
            } : {
              y: 0,
              rotate: 0
            }} transition={{
              duration: 0.2,
              ease
            }} data-id="element-255" data-mp="1jeo1xcmh6mqs" />
            </span>
          </button>
        </nav>

        <AnimatePresence data-id="element-256" data-mp="10i60g3htr8hc">
          {open && <NavMenu id="site-menu" onClose={() => setOpen(false)} data-id="element-257" data-mp="10j7pxczl7xil" />}
        </AnimatePresence>
      </div>
    </header>;
}